import React, { useCallback, useEffect, useState } from 'react';
import { adminMembersAPI, adminMemberUpdateAPI } from '../../api/admin';
import AdminMemberBizDialog from './AdminMemberBizDialog';
import { Card, Input, Select, Btn, Row, Table, Badge, Empty, Pager, Msg, fmtDate } from './ui';

// 회원 관리 — 검색·상태 필터(활성/신청 대기/반려/탈퇴), 기업회원 가입 신청 승인·반려, 개인/기업 구분·권한 변경,
// 기업 정보(사업자번호·정산일·거래처·할인률·마일리지·법인카드) 모달, 탈퇴 처리
const JOIN = { Email: '이메일', Kakao: '카카오', Naver: '네이버', Google: '구글' };
const STATUS = { active: ['on', '활성'], pending: ['dark', '신청 대기'], rejected: ['off', '반려'], quit: ['off', '탈퇴'] };

function AdminMembers({ me }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('active');
  const [msg, setMsg] = useState({ ok: false, text: '' });
  const [bizTarget, setBizTarget] = useState(null);
  const limit = 20;

  const load = useCallback(async (p = 1, query = q, st = status) => {
    const resp = await adminMembersAPI({ page: p, limit, q: query || undefined, status: st || undefined });
    if (resp && resp.status === 200) { setRows(resp.data.rows || []); setTotal(resp.data.total || 0); setPage(resp.data.page || p); }
  }, [q, status]);
  useEffect(() => { load(1, '', 'active'); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replace = (m) => setRows((prev) => prev.map((x) => (x.id === m.id ? m : x)));
  const update = async (m, data) => {
    setMsg({ ok: false, text: '' });
    const resp = await adminMemberUpdateAPI(m.id, data);
    if (resp && resp.status === 200) { replace(resp.data.member); return true; }
    setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '저장하지 못했습니다.' });
    return false;
  };
  const approve = async (m) => {
    if (!window.confirm(`${m.companyName || m.name} 기업회원 신청을 승인할까요? 승인되면 로그인할 수 있습니다.`)) return;
    if (await update(m, { status: 'active' })) setMsg({ ok: true, text: '승인했습니다. 고객에게 안내해 주세요.' });
  };
  const reject = async (m) => {
    if (!window.confirm(`${m.companyName || m.name} 신청을 반려할까요?`)) return;
    if (await update(m, { status: 'rejected' })) setMsg({ ok: true, text: '반려했습니다.' });
  };
  const quit = (m) => {
    if (!window.confirm(`${m.name || m.email} 회원을 탈퇴 처리할까요? 되돌릴 수 없습니다.`)) return;
    update(m, { status: 'quit' });
  };
  const search = (e) => { e.preventDefault(); load(1, q, status); };
  const isMe = (m) => me && m.id === me.userKey;

  return (
    <Card title="회원" desc={`${total}명`} action={(
      <form onSubmit={search}>
        <Row>
          <Select value={status} onChange={(e) => { setStatus(e.target.value); load(1, q, e.target.value); }} style={{ width: 130, height: 36 }}>
            <option value="active">활성</option>
            <option value="pending">신청 대기</option>
            <option value="rejected">반려</option>
            <option value="quit">탈퇴</option>
            <option value="">전체</option>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름 · 이메일 · 상호 · 연락처" style={{ width: 240, height: 36 }} />
          <Btn tone="ghost" size="sm" type="submit">검색</Btn>
        </Row>
      </form>
    )}>
      {bizTarget && <AdminMemberBizDialog member={bizTarget} onClose={() => setBizTarget(null)} onSaved={(m) => { replace(m); setBizTarget(m); }} />}
      <Msg ok={msg.ok}>{msg.text}</Msg>
      {!rows.length ? <Empty>회원이 없습니다.</Empty> : (
        <Table head={['회원', '연락처', '가입', '구분', '권한', '기업 정보', '상태', '']}>
          {rows.map((m) => {
            const [tone, label] = STATUS[m.status] || ['default', m.status];
            const biz = m.biz || null;
            return (
              <tr key={m.id}>
                <td>
                  <b style={{ display: 'block' }}>{m.name || '-'}{m.companyName ? ` · ${m.companyName}` : ''}{isMe(m) ? ' (나)' : ''}</b>
                  <small style={{ color: '#a4a4ac' }}>{m.email || '-'}</small>
                </td>
                <td>{m.phone || '-'}</td>
                <td><Badge>{JOIN[m.joinType] || m.joinType}</Badge><div style={{ fontSize: 12, color: '#a4a4ac', marginTop: 4 }}>{fmtDate(m.createdAt)}</div></td>
                <td>
                  <Select value={m.userType} disabled={m.status !== 'active'} onChange={(e) => update(m, { userType: e.target.value })} style={{ width: 100, height: 36 }}>
                    <option value="Private">개인</option>
                    <option value="Company">기업</option>
                  </Select>
                </td>
                <td>
                  <Select value={m.role} disabled={m.status !== 'active' || isMe(m)} onChange={(e) => update(m, { role: e.target.value })} style={{ width: 100, height: 36 }}>
                    <option value="member">회원</option>
                    <option value="admin">관리자</option>
                  </Select>
                </td>
                <td style={{ fontSize: 12, color: '#555558', maxWidth: 240 }}>
                  {biz ? (
                    <div>
                      <div>{biz.bizNo ? `사업자 ${biz.bizNo.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3')}` : '사업자번호 없음'} · 정산 {biz.settleLabel}</div>
                      <div style={{ color: '#a4a4ac' }}>{[biz.certUrl ? '사업자등록증 첨부' : '', biz.clientName && `거래처 ${biz.clientName}`, biz.discountRate ? `할인 ${biz.discountRate}%` : '', `마일리지 ${Number(m.mileage || 0).toLocaleString('ko-KR')}P`, biz.card ? `카드 ${biz.card.last4}` : ''].filter(Boolean).join(' · ')}</div>
                      <Btn tone="line" size="sm" style={{ marginTop: 6 }} onClick={() => setBizTarget(m)}>기업 정보</Btn>
                    </div>
                  ) : ([m.requester.company, m.requester.dept, m.requester.addr].filter(Boolean).join(' · ') || '-')}
                </td>
                <td><Badge tone={tone}>{label}</Badge></td>
                <td>
                  <Row>
                    {m.status === 'pending' && <Btn size="sm" onClick={() => approve(m)}>승인</Btn>}
                    {m.status === 'pending' && <Btn tone="danger" size="sm" onClick={() => reject(m)}>반려</Btn>}
                    {m.status === 'rejected' && <Btn size="sm" onClick={() => approve(m)}>승인</Btn>}
                    {m.status === 'active' && !isMe(m) && <Btn tone="danger" size="sm" onClick={() => quit(m)}>탈퇴 처리</Btn>}
                  </Row>
                </td>
              </tr>
            );
          })}
        </Table>
      )}
      <Pager page={page} limit={limit} total={total} onChange={(p) => load(p, q, status)} />
    </Card>
  );
}

export default AdminMembers;
